import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Building2, Clock, User, Shield, Loader2, Save, Sparkles } from 'lucide-react';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

export default function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    businessName: '',
    businessType: '',
    schedule: '',
    ownerName: '',
    email: ''
  });

  useEffect(() => {
    if (user) {
      setSettings({
        businessName: user.business_name || '',
        businessType: user.business_type || '',
        schedule: user.schedule || '09:00 - 21:00',
        ownerName: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    toast.success('Configuración guardada');
    setLoading(false);
  };

  const getPlanInfo = () => {
    const plan = user?.plan || 'trial';
    const config = {
      trial: { label: 'Prueba Gratuita', color: 'indigo', badge: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
      starter: { label: 'Starter', color: 'slate', badge: 'bg-white/10 text-white border-white/20' },
      pro: { label: 'Pro', color: 'purple', badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      business: { label: 'Business', color: 'emerald', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    };
    return config[plan] || config.trial;
  };

  const planInfo = getPlanInfo();

  return (
    <Layout>
      <motion.div 
        data-testid="settings-page" 
        className="space-y-8 max-w-3xl"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item}>
          <h1 className="text-headline text-white">Configuración</h1>
          <p className="text-caption mt-1">Administra tu cuenta y preferencias</p>
        </motion.div>

        {/* Plan Card */}
        <motion.div
          variants={item}
          className="nova-card p-6 bg-gradient-to-br from-indigo-500/8 to-purple-500/5 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-radial-gradient pointer-events-none opacity-20"
            style={{ background: 'radial-gradient(circle at top right, rgba(99,102,241,0.15) 0%, transparent 70%)' }}
          />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                <Shield className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-caption">Plan actual</p>
                <div className="flex items-center gap-3 mt-1">
                  <h3 className="text-title text-white">
                    {user?.plan === 'trial' ? 'Prueba Gratuita' : 
                     user?.plan === 'pro' ? 'Plan Pro' : 
                     user?.plan === 'business' ? 'Plan Business' : 'Plan Starter'}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${planInfo.badge}`}>
                    {planInfo.label}
                  </span>
                </div>
              </div>
            </div>
            <button className="nova-btn nova-btn-primary">
              <Sparkles className="w-4 h-4" />
              Mejorar plan
            </button>
          </div>
          {user?.trial_ends_at && user?.plan === 'trial' && (
            <p className="mt-4 text-sm text-[var(--nova-text-quaternary)] relative z-10">
              Tu prueba termina el {new Date(user.trial_ends_at).toLocaleDateString('es-MX', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </p>
          )}
        </motion.div>

        {/* Business Settings */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-indigo-400" />
            </div>
            <h3 className="text-title text-white">Información del Negocio</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre del negocio</label>
              <input
                type="text"
                value={settings.businessName}
                onChange={(e) => setSettings({ ...settings, businessName: e.target.value })}
                className="nova-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Tipo de negocio</label>
              <input
                type="text"
                value={settings.businessType}
                onChange={(e) => setSettings({ ...settings, businessType: e.target.value })}
                className="nova-input"
              />
            </div>
          </div>
        </motion.div>

        {/* Schedule Settings */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
            <h3 className="text-title text-white">Horario de Atención</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Horario</label>
            <input
              type="text"
              value={settings.schedule}
              onChange={(e) => setSettings({ ...settings, schedule: e.target.value })}
              placeholder="Ej: 09:00 - 21:00"
              className="nova-input"
            />
          </div>
        </motion.div>

        {/* Account Settings */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <User className="w-4 h-4 text-emerald-400" />
            </div>
            <h3 className="text-title text-white">Cuenta</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre</label>
              <input
                type="text"
                value={settings.ownerName}
                onChange={(e) => setSettings({ ...settings, ownerName: e.target.value })}
                className="nova-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Email</label>
              <input
                type="email"
                value={settings.email}
                disabled
                className="nova-input opacity-50 cursor-not-allowed"
              />
              <p className="text-xs text-[var(--nova-text-quaternary)] mt-1.5">El email no se puede cambiar</p>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div variants={item} className="flex justify-end pb-4">
          <button
            onClick={handleSave}
            disabled={loading}
            data-testid="save-settings-button"
            className="nova-btn nova-btn-primary px-8"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar cambios
          </button>
        </motion.div>
      </motion.div>
    </Layout>
  );
}
